import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { cdnChannel } from '@/client/cdn-sheets';
import { SeedClient, SERVER_SEED_KEYS, type ServerSeedKey } from '@/client/seed.client';
import DataComparisonViewer from '@/components/admin/DataComparisonViewer';

type SeedState = 'idle' | 'loading' | 'success' | 'error';

type SeedTarget = '__all__' | ServerSeedKey;

const PRESET_ENVS = ['prod', 'staging', 'dev'] as const;

const CATEGORY_OPTIONS: { value: SeedTarget; label: string }[] = [
  { value: '__all__', label: 'All' },
  ...SERVER_SEED_KEYS.map((k) => ({ value: k as SeedTarget, label: k })),
];

export default function SeedingPage() {
  const [seedState, setSeedState] = useState<SeedState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedEnv, setSelectedEnv] = useState<string>('staging');
  const [customEnv, setCustomEnv] = useState('');
  const [selectedTarget, setSelectedTarget] = useState<SeedTarget>('__all__');

  const activeEnv = customEnv.trim() || selectedEnv;
  const isAll = selectedTarget === '__all__';

  const handleSeed = async () => {
    setSeedState('loading');
    setErrorMessage('');
    try {
      if (!isAll) {
        await SeedClient.seedTargetFromAssets(activeEnv, selectedTarget as ServerSeedKey);
      } else {
        await SeedClient.seedFromAssets(activeEnv);
      }
      setSeedState('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      setSeedState('error');
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">Master Data Seeding</h2>
        <p className="text-sm text-muted-foreground">
          Fetch sheet data from CDN, filter to server-validation-only fields, and seed the game
          server. Stages are stored as compressed tuples in <code>$global.state</code> under{' '}
          <code>{'s_<id>'}</code> keys; other categories go to V8 collections. Excluded: skills,
          consumables (client-only).
        </p>
      </header>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Seeding</CardTitle>
            <CardDescription>Pick env + collection, then seed.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">Environment</Label>
              <div className="flex gap-2">
                {PRESET_ENVS.map((env) => {
                  const isActive = activeEnv === env && !customEnv.trim();
                  return (
                    <Button
                      key={env}
                      type="button"
                      variant={isActive ? 'default' : 'secondary'}
                      onClick={() => {
                        setSelectedEnv(env);
                        setCustomEnv('');
                      }}
                      className={cn('flex-1', isActive && 'pointer-events-none')}
                    >
                      {env}
                    </Button>
                  );
                })}
              </div>
              <Input
                type="text"
                placeholder="Custom environment (e.g. jiwon)"
                value={customEnv}
                onChange={(e) => setCustomEnv(e.target.value)}
              />
              {activeEnv && (
                <p className="text-xs text-muted-foreground">
                  Path:{' '}
                  <span className="font-mono text-primary">rb-sheets/{cdnChannel(activeEnv)}/</span>
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">Target</Label>
              <Select
                value={selectedTarget}
                onValueChange={(v) => setSelectedTarget(v as SeedTarget)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              onClick={handleSeed}
              disabled={seedState === 'loading' || !activeEnv}
            >
              {seedState === 'loading'
                ? 'Seeding…'
                : !isAll
                  ? `Seed ${selectedTarget} (${activeEnv})`
                  : `Seed all data (${activeEnv})`}
            </Button>

            {seedState === 'success' && (
              <Alert>
                <AlertDescription className="text-green-500">
                  Seeding completed successfully.
                </AlertDescription>
              </Alert>
            )}
            {seedState === 'error' && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Data Comparison Viewer</CardTitle>
            <CardDescription>
              Compare seeded DB data with filtered JSON from sheets (server-only fields). Uses the
              same environment as above.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataComparisonViewer env={activeEnv} />
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
